import React, { memo } from 'react';
import { Box, Paper, Typography, Button } from '@mui/material';

interface BulkEditAction {
  /** Unique key for the action */
  key: string;
  /** Display label for the button */
  label: string;
  /** Callback when button is clicked */
  onClick: () => void;
}

interface BulkEditToolbarProps {
  /** Whether to show the toolbar (typically when items are selected) */
  visible: boolean;
  /** List of edit actions to display as buttons */
  actions: BulkEditAction[];
  /** Callback to clear selection */
  onClearSelection: () => void;
  /** Background color for the toolbar */
  bgcolor?: string;
  /** Optional label prefix (default: "Bulk edit:") */
  label?: string;
}

/**
 * A reusable toolbar for bulk edit operations.
 * Displays a row of action buttons with a clear selection option.
 */
const BulkEditToolbar = memo(function BulkEditToolbar({
  visible,
  actions,
  onClearSelection,
  bgcolor = '#e3f2fd',
  label = 'Bulk edit:',
}: BulkEditToolbarProps) {
  if (!visible) return null;

  return (
    <Paper sx={{ p: 1.5, mb: 2, bgcolor }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ mr: 1 }}>
          {label}
        </Typography>
        {actions.map((action) => (
          <Button
            key={action.key}
            size="small"
            variant="outlined"
            onClick={action.onClick}
          >
            {action.label}
          </Button>
        ))}
        <Button size="small" color="inherit" onClick={onClearSelection}>
          Clear
        </Button>
      </Box>
    </Paper>
  );
});

export default BulkEditToolbar;
